import { useMemo } from "react"
import { dateFormat, flatJoin, numberFormat } from "shared/util"
import { hiddenFlags } from 'features/search/util'
import { useUserSession } from "features/session/context"
import { SearchDetailResult } from "interface/search"
import { SisetuResult } from "interface/sisetu"
import { KojinOrgan, KojinResult } from "interface/kojin"

import { GridColumnDefMap } from "components/grid/types"
import { columnBase, formatter } from "components/grid/util"
import { renderer } from "components/grid/renderer"

import { JournalERAMap } from "../../types"

export type SisetuWithKolResult = {
	sisetu: SisetuResult
	kojin?: KojinResult
	organ?: KojinOrgan
}
type useMixedGridColumnsProps = {
	search: SearchDetailResult
}
export const useMixedGridColumns = ({
	search
}: useMixedGridColumnsProps) => {
	const session = useUserSession()
	const {
		hideResult,
		hideDPC,
		hideCancer,
		hideJournal,
		hideTrial,
		//hideResearch,
		hideSenmoni,
		hideGuideLine
	} = useMemo(
		() => hiddenFlags(search),
		[search]
	)
	const columnsDef = useMemo<GridColumnDefMap<SisetuWithKolResult>>(
		() => {
			const columnsDef: GridColumnDefMap<SisetuWithKolResult> = {
				'rank': {
					headerName: '総合順位',
					hidden: hideResult,
					value: (r) => r.sisetu.score?.rank,
					hideable: false,
					...columnBase.rank
				},
				'sisetuName': {
					headerName: '施設名',
					width: 400,
					hideable: false,
					sticky: true,
					value: (r) => [
						r.sisetu.sisetuNameKana ?? '',
						r.sisetu.sisetuName ?? ''
					],
					hasKana: true,
					filterType: 'text'
				},
				'ryakusikiSisetuName': {
					headerName: '施設名(略式)',
					width: 200,
					value: (r) => [
						r.sisetu.ryakusikiSisetuNameKana ?? '',
						r.sisetu.ryakusikiSisetuName ?? ''
					],
					defaultShow: false,
					hasKana: true,
					filterType: 'text'
				},
				'block': {
					headerName: 'ブロック',
					width: 100,
					value: (r) => r.sisetu.juusyoBlockName,
					filterType: 'candidates'
				},
				'todofuken': {
					headerName: '都道府県',
					defaultShow: false,
					width: 100,
					value: (r) => r.sisetu.juusyoKenName,
					filterType: 'candidates'
				},
				'iryoken': {
					headerName: '医療圏',
					width: 100,
					defaultShow: false,
					value: (r) => r.sisetu.iryoukenName,
					filterType: 'candidates'
				},
				'city': {
					headerName: '市区町村',
					width: 100,
					defaultShow: false,
					value: (r) => r.sisetu.cityName,
					filterType: 'candidates'
				},
				'juusyo': {
					headerName: '施設住所',
					width: 300,
					defaultShow: false,
					value: (r) => r.sisetu.juusyo,
					filterType: 'text'
				},
				'bedCount': {
					headerName: '病床数',
					defaultShow: false,
					value: (r) => r.sisetu.bedCount?.ippan,
					filterType: 'range',
					suffix: '床',
					...columnBase.bedCount
				},
				'byouinSyubetu': {
					headerName: '種別',
					width: 70,
					defaultShow: false,
					value: (r) => r.sisetu.byouinSyubetu?.shortName,
					filterType: 'candidates'
				},
				'keieitai': {
					headerName: '経営体',
					width: 70,
					defaultShow: false,
					value: (r) => r.sisetu.keieitai?.shortName,
					filterType: 'candidates'
				},
				'sisetuKubun': {
					headerName: '施設区分',
					width: 120,
					value: (r) => r.sisetu.sisetuKubun?.name,
					filterType: 'candidates'
				},
				'hospitalFlags': {
					headerName: '施設機能',
					width: 160,
					value: (r) => r.sisetu.hospitalFlags?.map(flag => flag.functionName).join('\n'),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'kojinCount': {
					headerName: 'DCF医師数',
					defaultShow: false,
					value: (r) => r.sisetu.kojinCount,
					filterType: 'range',
					...columnBase.personCount
				},
				'pidCount': {
					headerName: 'CN会員数',
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.sisetu.pidCount,
					filterType: 'range',
					...columnBase.personCount
				},
				'pidRate': {
					headerName: 'CN会員率',
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => (r.sisetu.kojinCount ?? 0) > 0 ? ((r.sisetu.pidCount ?? 0) / (r.sisetu.kojinCount ?? 0)) : null,
					align: 'right',
					width: 80,
					type: 'number',
					valueFormatter: (value: number | string) => {
						if (typeof value !== 'number') return
						return (value * 100).toFixed(2) + ' %'
					}
				},
				'senmoniScore': {
					headerName: '専門医スコア',
					hidden: hideResult || hideSenmoni,
					value: (r) => r.sisetu.score?.senmoni?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { senmoniRank?: number }) => {
						return (value && value !== Infinity && row.senmoniRank && row.senmoniRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.senmoniRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'senmoniRank',
					filterType: 'histgram'
				},
				'senmoniRank': {
					headerName: '専門医順位',
					hidden: true,
					defaultShow: false,
					value: (r) => r.sisetu.score?.senmoni?.rank,
					...columnBase.rank,
				},
				'dpcScore': {
					headerName: 'DPCスコア',
					hidden: hideResult || hideDPC,
					value: (r) => r.sisetu.score?.dpc?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { dpcRank?: number }) => {
						return (value && value !== Infinity && row.dpcRank && row.dpcRank !== Infinity) ? (
							`${numberFormat(value)}件 (${row.dpcRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'dpcRank',
					filterType: 'histgram',
					histgram: { unit: 100 }
				},
				'dpcRank': {
					headerName: 'DPC順位',
					hidden: true,
					value: (r) => r.sisetu.score?.dpc?.rank,
					...columnBase.rank
				},
				'cancerScore': {
					headerName: 'がんスコア',
					hidden: hideResult || hideCancer,
					value: (r) => r.sisetu.score?.cancer?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { cancerRank?: number }) => {
						return (value && value !== Infinity && row.cancerRank && row.cancerRank !== Infinity) ? (
							`${numberFormat(Math.floor(value * 100) / 100)}pt (${row.cancerRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'cancerRank',
					filterType: 'histgram'
				},
				'cancerRank': {
					headerName: 'がん順位',
					hidden: true,
					value: (r) => r.sisetu.score?.cancer?.rank,
					...columnBase.rank
				},
				'trialScore': {
					headerName: '治験・研究スコア',
					hidden: hideResult || hideTrial,
					value: (r) => r.sisetu.score?.trial?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { trialRank?: number }) => {
						return (value && value !== Infinity && row.trialRank && row.trialRank !== Infinity) ? (
							`${numberFormat(value)}件 (${row.trialRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'trialRank',
					filterType: 'histgram'
				},
				'trialRank': {
					headerName: '治験・研究順位',
					hidden: true,
					value: (r) => r.sisetu.score?.trial?.rank,
					...columnBase.rank
				},
				'sisetuCode': {
					headerName: 'DCF施設コード',
					width: 100,
					defaultShow: false,
					value: (r) => r.sisetu.sisetuCode,
					filterType: 'text'
				},
				'institutionalCode': {
					headerName: '施設コード',
					width: 120,
					defaultShow: false,
					value: (r) => r.sisetu.institutionalCode,
					filterType: 'text'
				},
				'kojin_rank': {
					headerName: '医師総合順位',
					hidden: hideResult,
					value: (r) => r.kojin?.score?.rank,
					hideable: false,
					...columnBase.rank
				},
				'isiName': {
					headerName: '医師名',
					width: 150,
					hideable: false,
					sticky: true,
					value: (r) => [
						r.kojin?.isiNameKana ?? '',
						r.kojin?.isiName ?? ''
					],
					hasKana: true,
					filterType: 'text'
				},
				'seibetu': {
					headerName: '性別',
					width: 50,
					defaultShow: false,
					value: (r) => r.kojin?.seibetuCode === '1' ? '男性' : r.kojin?.seibetuCode === '2' ? '女性' : '不明',
					filterType: 'candidates'
				},
				'age': {
					headerName: '年齢',
					value: (r) => r.kojin?.nenrei,
					...columnBase.age
				},
				'birthDate': {
					headerName: '生年月日',
					width: 196,
					defaultShow: false,
					value: (r) => r.kojin?.seinenGappi,
					valueFormatter: (value) => dateFormat(value),
					filterType: 'date'
				},
				'organ': {
					headerName: '部課',
					width: 400,
					value: (r) => flatJoin('\t', r.organ?.bukaName, r.organ?.yakusyokuName)
				},
				'senmoni': {
					headerName: '専門医',
					width: 200,
					value: (r) => r.kojin?.senmoni?.flatMap(senmoni => senmoni.senmoniName ? (
						senmoni.sidoui ? `${senmoni.senmoniName}指導医` :
						senmoni.senmoni ? `${senmoni.senmoniName}専門医` :
						senmoni.ninteii ? `${senmoni.senmoniName}認定医` : senmoni.senmoniName
					) : []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'gakkai': {
					headerName: '所属学会',
					width: 200,
					defaultShow: false,
					value: (r) => r.kojin?.gakkai?.flatMap(gakkai => gakkai.gakkaiName ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'sotukou': {
					headerName: '出身大学',
					width: 100,
					value: (r) => r.kojin?.sotukouName,
					filterType: 'candidates'
				},
				'sotuNen': {
					headerName: '卒業年',
					align: 'right',
					defaultShow: false,
					width: 80,
					valueFormatter: formatter.year,
					value: (r) => r.kojin?.sotugyouYear,
					filterType: 'histgram'
				},
				'primarySinryoka': {
					headerName: 'COM診療科',
					width: 100,
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.kojin?.primarySinryoka?.sinryokaName,
					filterType: 'candidates'
				},
				/*
				'comSinryoka': {
					headerName: 'COM診療科',
					width: 200,
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.kojin?.comSinryoka?.flatMap(sinryoka => sinryoka.sinryokaName ?? []),
					renderCell: (params) => renderer.multiRow(params.value),
					filterType: 'candidates'
				},
				*/
				'tierRank': {
					headerName: 'Tierランク',
					width: 80,
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.kojin?.mrpStats?.rank?.name,
					filterType: 'candidates'
				},
				'guidelineScore': {
					headerName: 'ガイドラインスコア',
					hidden: hideResult || hideGuideLine,
					value: (r) => r.kojin?.score?.guideline?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { guidelineRank?: number }) => {
						return (value && value !== Infinity && row.guidelineRank && row.guidelineRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.guidelineRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'guidelineRank',
					filterType: 'range'
				},
				'guidelineRank': {
					headerName: 'ガイドライン順位',
					hidden: true,
					value: (r) => r.kojin?.score?.guideline?.rank,
					...columnBase.rank
				},
				'journalScore': {
					headerName: '論文スコア',
					hidden: hideResult || hideJournal,
					value: (r) => r.kojin?.score?.journal?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { journalRank?: number, journalScore: number }) => {
						return (value && value !== Infinity && row.journalRank && row.journalRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.journalRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'journalRank',
					filterType: 'range'
				},
				'journalRank': {
					headerName: '論文順位',
					hidden: true,
					value: (r) => r.kojin?.score?.journal?.rank,
					...columnBase.rank
				},
				'journalERA': {
					headerName: '論文作成時期',
					hidden: hideResult || hideJournal,
					value: (r) => {
						if (r.kojin?.score?.journal) {
							const scores = [
								{ label: 'F', score: r.kojin.score.journal.farTimeScore ?? 0 }, 
								{ label: 'M', score: r.kojin.score.journal.middleTimeScore ?? 0 },
								{ label: 'N', score: r.kojin.score.journal.nearTimeScore ?? 0 }
							].filter(s => s.score > 0)
							if (scores.length > 0) {
								scores.sort((a, b) => b.score - a.score)
								/*
								const set = scores.map((s, i, arr) => (
									i === 0 ? s.label : arr[i-1].score === s.score ? `=${s.label}` : `>${s.label}`
								)).join('')
								return set
								*/
								return JournalERAMap[scores.map(s => s.label).join('>')] ?? null
							}
						}
						return null
					},
					filterType: 'candidates'
				},
				'journalFarTimesCount': {
					headerName: '古論文件数',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.kojin?.score?.journal?.farTimeCount,
					...columnBase.count
				},
				'journalMiddleTimesCount': {
					headerName: '中論文件数',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.kojin?.score?.journal?.middleTimeCount,
					...columnBase.count
				},
				'journalNearTimesCount': {
					headerName: '新論文件数',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.kojin?.score?.journal?.nearTimeCount,
					...columnBase.count
				},
				'journalFarTimesRate': {
					headerName: '古論文割合',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => {
						if (r.kojin?.score?.journal && (r.kojin?.score.journal.farTimeCount ?? 0) > 0) {
							return (r.kojin?.score.journal.farTimeScore ?? 0) / (
								(r.kojin?.score.journal.farTimeScore ?? 0) +
								(r.kojin?.score.journal.middleTimeScore ?? 0) +
								(r.kojin?.score.journal.nearTimeScore ?? 0)
							)
						}
						return null
					},
					...columnBase.percent
				},
				'journalMiddleTimesRate': {
					headerName: '中論文割合',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => {
						if (r.kojin?.score?.journal && (r.kojin?.score.journal.middleTimeScore ?? 0) > 0) {
							return (r.kojin?.score.journal.middleTimeScore ?? 0) / (
								(r.kojin?.score.journal.farTimeScore ?? 0) +
								(r.kojin?.score.journal.middleTimeScore ?? 0) +
								(r.kojin?.score.journal.nearTimeScore ?? 0)
							)
						}
						return null
					},
					...columnBase.percent
				},
				'journalNearTimesRate': {
					headerName: '新論文割合',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => {
						if (r.kojin?.score?.journal && (r.kojin?.score.journal.nearTimeScore ?? 0) > 0) {
							return (r.kojin?.score.journal.nearTimeScore ?? 0) / (
								(r.kojin?.score.journal.farTimeScore ?? 0) +
								(r.kojin?.score.journal.middleTimeScore ?? 0) +
								(r.kojin?.score.journal.nearTimeScore ?? 0)
							)
						}
						return null
					},
					...columnBase.percent
				},
				'single': {
					headerName: '単独論文',
					hidden: hideResult || hideJournal,
					value: (r) => r.kojin?.score?.journal?.counts.single,
					...columnBase.count
				},
				'first': {
					headerName: '筆頭著者',
					hidden: hideResult || hideJournal,
					value: (r) => r.kojin?.score?.journal?.counts.first,
					...columnBase.count
				},
				'second': {
					headerName: '第二著者',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.kojin?.score?.journal?.counts.second,
					...columnBase.count
				},
				'last': {
					headerName: '責任著者',
					hidden: hideResult || hideJournal,
					value: (r) => r.kojin?.score?.journal?.counts.last,
					...columnBase.count
				},
				'lastSecond': {
					headerName: '第二責任著者',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.kojin?.score?.journal?.counts.lastSecond,
					...columnBase.count
				},
				'total': {
					headerName: '合計論文数',
					hidden: hideResult || hideJournal,
					value: (r) => r.kojin?.score?.journal?.counts.total,
					...columnBase.count
				},
				'kojinCode': {
					headerName: 'DCF個人コード',
					width: 80,
					defaultShow: false,
					value: (r) => r.kojin?.kojinCode
				},
				'personalId': {
					headerName: 'PID',
					hidden: session.isCareNet === false,
					width: 80,
					defaultShow: false,
					value: (r) => r.kojin?.personalId
				}
			}
			if (hideResult === false && hideGuideLine === false) {
				const DEF = '__default_'
				const sisetuGuideLineTypes: string[] = []
				const kojinGuideLineTypes: string[] = []
				search.guideLine?.forEach(guideLine => {
					const guideLineType = guideLine.guideLineType ?? DEF
					if (guideLine.hasHospital && sisetuGuideLineTypes.includes(guideLineType) === false) {
						sisetuGuideLineTypes.push(guideLineType)
					}
					if (guideLine.hasDoctor && sisetuGuideLineTypes.includes(guideLineType) === false) {
						kojinGuideLineTypes.push(guideLineType)
					}
				})
				sisetuGuideLineTypes.sort((a, b) => a === DEF ? -1 : b === DEF ? 1 : a.localeCompare(b))
				kojinGuideLineTypes.sort((a, b) => a === DEF ? -1 : b === DEF ? 1 : a.localeCompare(b))

				sisetuGuideLineTypes.forEach((guideLineType) => {
					const field = guideLineType === DEF ? 'guideLineSisetu' : `guideLineSisetu-${guideLineType}`
					columnsDef[field] = {
						headerName: guideLineType === DEF ? '施設ガイドライン' : guideLineType,
						width: 300,
						limitRow: 5,
						value: (r) => r.sisetu.guideLine?.flatMap(
							guide => {
								const type = search.guideLine?.find(
									guideLine => guideLine.guideLineId === guide.guideLineId
								)?.guideLineType ?? null
								if (guideLineType === DEF) {
									if (type !== null) return []
								} else {
									if (type !== guideLineType) return []
								}
								if (guide.score === 0) return []
								return flatJoin('\t',
									guide.guideLineName?.replaceAll('　', ' '),
									guide.hospitalClass?.replaceAll('　', ' ')
								)
							}
						),
						renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
						filterType: "text"
					}
				})
				kojinGuideLineTypes.forEach((guideLineType) => {
					const field = guideLineType === DEF ? 'guideLineKojin' : `guideLineKojin-${guideLineType}`
					columnsDef[field] = {
						headerName: guideLineType === DEF ? '医師ガイドライン' : guideLineType,
						width: 300,
						limitRow: 5,
						value: (r) => r.kojin?.guideLine?.flatMap(
							guide => {
								const type = search.guideLine?.find(
									guideLine => guideLine.guideLineId === guide.guideLineId
								)?.guideLineType ?? null
								if (guideLineType === DEF) {
									if (type !== null) return []
								} else {
									if (type !== guideLineType) return []
								}
								if (guide.score === 0) return []
								return flatJoin('\t',
									guide.guideLineName?.replaceAll('　', ' '),
									guide.researcherClass,
									guide.postNames
								)
							}
						),
						renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
						filterType: "text"
					}
				})
			}

			return columnsDef
		},
		[search, session, hideResult, hideDPC, hideCancer, hideSenmoni, hideJournal, hideTrial, hideGuideLine]
	)
	return { columnsDef }
}